import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { saveUserProfile } from '../utils/userPreferences';
import { markAsVisited, getGuestId } from '../services/analytics';

const SignupPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    age: '',
    password: '',
    confirmPassword: ''
  });

  // Hide Emergent badge on this page
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    return () => {
      if (badge) {
        badge.style.display = 'flex';
      }
    };
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    // Validation
    if (!formData.name || !formData.email || !formData.password) {
      setErrorMessage('Please fill in name, email, and password');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          age: formData.age ? parseInt(formData.age) : null,
          password: formData.password,
          device_id: getGuestId()  // Send device ID
        })
      });

      // Check status before trying to read body
      if (response.ok) {
        try {
          const data = await response.json();
          // Mark as visited
          markAsVisited();
          
          // Save profile to local storage
          saveUserProfile({
            name: formData.name,
            email: formData.email,
            mobile: formData.mobile,
            age: formData.age
          });

          // Store auth token
          localStorage.setItem('smartreadgo_auth_token', 'authenticated');

          toast({
            title: 'Account Created!',
            description: 'Welcome to SmartReadGo!'
          });

          navigate('/');
        } catch (jsonError) {
          console.error('Success response parse error:', jsonError);
          // If we can't parse success response, still navigate
          navigate('/');
        }
      } else {
        // For error responses, try to get the error message
        try {
          const data = await response.json();
          setErrorMessage(data.detail || 'Could not create account');
        } catch (jsonError) {
          // If we can't parse the error, show a generic message based on status
          if (response.status === 400) {
            setErrorMessage('Email already registered or invalid data');
          } else {
            setErrorMessage('Signup failed. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrorMessage('Connection error. Please check your internet and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Safe area padding for mobile status bar */}
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/login')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Create Account</h1>
            <p className="text-sm text-gray-600">Join SmartReadGo</p>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="px-6 py-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-900 font-medium">Full Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-900 font-medium">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
              required
            />
          </div>

          {/* Mobile */}
          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-gray-900 font-medium">Mobile Number (Optional)</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="Enter your mobile number"
              value={formData.mobile}
              onChange={(e) => handleChange('mobile', e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
            />
          </div>

          {/* Age */}
          <div className="space-y-2">
            <Label htmlFor="age" className="text-gray-900 font-medium">Age (Optional)</Label>
            <Input
              id="age"
              type="number"
              placeholder="Enter your age"
              value={formData.age}
              onChange={(e) => handleChange('age', e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-900 font-medium">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className="text-base py-6 pr-12 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-gray-900 font-medium">Confirm Password *</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
              required
            />
          </div>

          {/* Error Message Display */}
          {errorMessage && (
            <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full mt-6"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        {/* Login Link */}
        <div className="text-center mt-6">
          <span className="text-gray-600">Already have an account? </span>
          <button
            onClick={() => navigate('/login')}
            className="text-blue-700 font-semibold hover:underline"
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
